import { PaginationMetadata } from '@execonline-inc/resource';
import { Maybe, just } from 'maybeasy';
import { currentUserStore } from '../CurrentUserStore';
import { Error } from '../ErrorHandling';
import { SearchParameter, SearchParameters } from '../ListFilter/Types';
import ParticipantStore from '../ParticipantStore';
import { Resource, ResourceWithMetadata } from '../Resource/Types';
import { TPlainTextKey } from '../Translations';
import { RegistrationType } from '../components/LearningCollections/LearningCollection/Invitations/Store/Types';
import { StringOption } from '../components/Options/Types';

interface Initializing {
  kind: 'initializing';
  searchParameters: SearchParameters;
  selectedOrganization: Maybe<StringOption>;
}

export interface Loading {
  kind: 'loading';
  page: number;
  searchParameters: SearchParameters;
  selectedOrganization: Maybe<StringOption>;
}

interface ReadyState {
  page: number;
  searchParameters: SearchParameters;
  selectedOrganization: Maybe<StringOption>;
  resource: ParticipantsResource;
  participantStores: ReadonlyArray<ParticipantStore>;
}

export interface Ready extends ReadyState {
  kind: 'ready';
}

export interface UpdatingQuery extends ReadyState {
  kind: 'updating-query';
}

export interface UpdatingSearchParameters extends ReadyState {
  kind: 'updating-search-parameters';
  searchParameters: SearchParameters;
  selectedOrganization: Maybe<StringOption>;
}

export interface Sorting extends ReadyState {
  kind: 'sorting';
}

export interface LoadingMore extends ReadyState {
  kind: 'loading-more';
}

export interface LoadedMore {
  resource: ParticipantsResource;
  participantStores: ReadonlyArray<ParticipantStore>;
}

export const initializing = (searchParameters: SearchParameters): Initializing => ({
  kind: 'initializing',
  selectedOrganization: currentUserStore.organizationOption,
  searchParameters,
});

export const loading = (
  page: number,
  state: Initializing | Sorting | Ready | UpdatingSearchParameters,
): Loading => {
  const { kind, ...previous } = state;
  return {
    kind: 'loading',
    ...previous,
    page: page,
    selectedOrganization: state.selectedOrganization,
  };
};

export const loadingMore = (page: number, state: Ready): LoadingMore => {
  const { kind, ...previous } = state;
  return {
    kind: 'loading-more',
    ...previous,
    page: page,
    selectedOrganization: state.selectedOrganization,
  };
};

export const ready = (
  state: Loading | UpdatingQuery | UpdatingSearchParameters | LoadingMore,
  resource: ParticipantsResource,
  participantStores: ReadonlyArray<ParticipantStore>,
): Ready => {
  const { kind, ...previous } = state;
  return {
    kind: 'ready',
    ...previous,
    resource,
    participantStores,
    selectedOrganization: state.selectedOrganization,
  };
};

const updatedSearchParameters = (
  searchParameters: SearchParameters,
  searchParameter: SearchParameter,
) => searchParameters.filter((sp) => sp.key !== searchParameter.key).concat([searchParameter]);

export const updatingSearchParameters = (
  searchParameters: SearchParameters,
  state: Ready,
): UpdatingSearchParameters => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-search-parameters',
    ...previous,
    searchParameters,
  };
};

export const updatingOrganizationParameters = (
  state: Ready,
  option: StringOption,
): UpdatingSearchParameters => {
  const { kind, ...previous } = state;

  return {
    kind: 'updating-search-parameters',
    ...previous,
    searchParameters: updatedSearchParameters(state.searchParameters, {
      key: 'organization_id',
      value: option.value,
    }),
    selectedOrganization: just(option),
  };
};

export const updatingQuery = (searchParameters: SearchParameters, state: Ready): UpdatingQuery => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-query',
    ...previous,
    searchParameters,
  };
};

export const sorting = (searchParameters: SearchParameters, state: Ready): Sorting => {
  const { kind, ...previous } = state;

  return {
    kind: 'sorting',
    ...previous,
    searchParameters: searchParameters,
  };
};

export type State =
  | Initializing
  | Loading
  | LoadingMore
  | Ready
  | UpdatingQuery
  | UpdatingSearchParameters
  | Sorting
  | Error;

export type ConsumptionStatus = 'Allocated' | 'Activated';

export interface UserProductLicense {
  id: number;
  name: string;
  startOn: Date;
  endOn: Date;
  consumptionStatus: ConsumptionStatus;
  registrationType: RegistrationType;
}

export type UserProductLicenseResource = Resource<UserProductLicense>;

export type LicenseStatus = ('Expired' | ConsumptionStatus) & TPlainTextKey;

export const licenseStatus = (userProductLicense: UserProductLicenseResource): LicenseStatus =>
  userProductLicense.payload.endOn < new Date()
    ? 'Expired'
    : userProductLicense.payload.consumptionStatus;

export type UserStatus = 'active' | 'disabled';

export interface Participant {
  kind: 'participant';
  id: number;
  fullName: Maybe<string>;
  initials: Maybe<string>;
  email: string;
  organization: string;
  preferredLanguage: Maybe<string>;
  loginCount: number;
  lastLoginAt: Maybe<Date>;
  collections: string[];
  userGroups: string[];
  userGroupsCount: number;
  activatedLicenseCount: number;
  licenses: ReadonlyArray<UserProductLicenseResource>;
  status: UserStatus;
}
export type ParticipantResource = Resource<Participant>;

export interface Participants {
  participants: ParticipantResource[];
}

export type ParticipantsResource = ResourceWithMetadata<Participants, PaginationMetadata>;

export type SortableColumn =
  | 'name'
  | 'email'
  | 'organization'
  | 'preferred_language'
  | 'sign_in_count'
  | 'last_sign_in_at';

export type CheckedParticipantsState = 'all-checked' | 'some-checked' | 'none-checked';
